import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Filter, Calendar, User, MoreVertical, Edit, Trash2, Clock, Save, X, MessageSquare, Sparkles } from 'lucide-react';
import CommentThread from '@/components/CommentThread';
import { 
  AITaskBreakdown, 
  AISuggestAssignee, 
  AICommentSummary, 
  AISentimentIndicator, 
  AITaskPrediction, 
  AIStatusUpdate 
} from '@/components/AITaskFeatures';
import { useVoiceTaskDrafts } from '@/hooks/useVoiceTaskDrafts';
import { VoiceCaptureButton } from '@/components/ai/VoiceCaptureButton';
import { TaskDraftList } from '@/components/ai/TaskDraftList';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ListenButton } from '@/components/ListenButton';
import { TaskMeta, SubtaskChecklist, TaskExtrasEditor, extrasPayload, taskSpeechText } from '@/components/tasks/TaskExtras';
import '@/styles/ai.css';
import './styles/KanbanView.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EMPTY_TASK = { title: '', description: '', assigned_to: '', status: 'todo', priority: null, estimated_hours: '', due_date: '', subtasks: [] };
const editableExtras = (task) => ({ priority: task.priority || null, estimated_hours: task.estimated_hours ?? '', due_date: task.due_date || '', subtasks: task.subtasks || [] });

export default function KanbanView({ user }) {
  const [tasks, setTasks] = useState({ todo: [], 'in-progress': [], done: [] });
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [newTask, setNewTask] = useState(EMPTY_TASK);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState({ title: '', description: '', status: '', ...editableExtras({}) });
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [showAIBreakdown, setShowAIBreakdown] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const isUpdatingRef = useRef(false);
  const voiceCapture = useVoiceTaskDrafts({ teamId: selectedTeam, onError: (msg) => toast.error(msg) });
  
  const isOwner = currentTeam && user && currentTeam.owner_id === user.id;

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      // Update currentTeam when selectedTeam changes
      const team = teams.find(t => t.id === selectedTeam);
      if (team) {
        setCurrentTeam(team);
      }
      
      fetchTasks();
      fetchTeamMembers();
    }
  }, [selectedTeam, teams]);

  // Listen for task updates from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'tasks_updated' && selectedTeam && !isUpdatingRef.current) {
        console.log('KanbanView: Refetching tasks due to storage change');
        fetchTasks();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen to custom event for same-window updates
    const handleTaskUpdate = () => {
      if (selectedTeam && !isUpdatingRef.current) {
        console.log('KanbanView: Refetching tasks due to custom event');
        fetchTasks();
      }
    };
    
    window.addEventListener('taskUpdated', handleTaskUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('taskUpdated', handleTaskUpdate);
    };
  }, [selectedTeam]);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
      if (response.data.length > 0) {
        setSelectedTeam(response.data[0].id);
        setCurrentTeam(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch teams');
    }
  };

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/tasks?team_id=${selectedTeam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const grouped = { todo: [], 'in-progress': [], done: [] };
      response.data.forEach(task => {
        if (grouped[task.status]) {
          grouped[task.status].push(task);
        }
      });
      setTasks(grouped);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams/${selectedTeam}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team members');
    }
  };

  const createTask = async () => {
    if (!newTask.title.trim()) { toast.error('Give the task a title'); return; }
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/tasks`, {
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        team_id: selectedTeam,
        assigned_to: newTask.assigned_to || null,
        status: newTask.status || 'todo',
        ...extrasPayload(newTask),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isOwner ? 'Task created successfully' : 'Task submitted for approval');
      setIsDialogOpen(false);
      setNewTask(EMPTY_TASK);
      fetchTasks();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
    return null;
  };

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Block drag-drop for non-owners
    if (!isOwner && source.droppableId !== destination.droppableId) {
      toast.error('Only team owners can change task status via drag & drop. Your changes must be approved.');
      return;
    }

    const sourceColumn = [...tasks[source.droppableId]];
    const destColumn = source.droppableId === destination.droppableId ? sourceColumn : [...tasks[destination.droppableId]];
    const [movedTask] = sourceColumn.splice(source.index, 1);

    if (source.droppableId !== destination.droppableId) {
      movedTask.status = destination.droppableId;
      destColumn.splice(destination.index, 0, movedTask);

      setTasks({
        ...tasks,
        [source.droppableId]: sourceColumn,
        [destination.droppableId]: destColumn
      });

      try {
        isUpdatingRef.current = true;
        const token = localStorage.getItem('session_token');
        const response = await axios.patch(`${API}/tasks/${draggableId}`, 
          { status: destination.droppableId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.pending) {
          toast.info('Change submitted for approval');
        } else {
          toast.success('Task moved successfully');
        }
        
        // Notify other components (but not this one)
        console.log('KanbanView: Dispatching task update event after drag');
        localStorage.setItem('tasks_updated', Date.now().toString());
        window.dispatchEvent(new Event('taskUpdated'));
        
        // Reset flag after a short delay
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 500);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update task');
        isUpdatingRef.current = false;
        fetchTasks();
      }
    } else {
      destColumn.splice(destination.index, 0, movedTask);
      setTasks({
        ...tasks,
        [source.droppableId]: destColumn
      });
    }
  };

  const openTaskDetails = (task) => {
    setSelectedTask(task);
    setEditedTask({ title: task.title, description: task.description, status: task.status, ...editableExtras(task) });
    setIsDetailsOpen(true);
    setIsEditing(false);
    setIsEditingDetails(false);
  };

  const updateTaskDetails = async () => {
    try {
      isUpdatingRef.current = true;
      const token = localStorage.getItem('session_token');
      
      const response = await axios.put(`${API}/tasks/${selectedTask.id}/details`, {
        title: editedTask.title,
        description: editedTask.description,
        ...extrasPayload(editedTask),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.pending) {
        toast.info('Changes submitted for approval');
      } else {
        toast.success('Task details updated successfully');
      }
      
      setIsEditingDetails(false);
      await fetchTasks();
      setIsDetailsOpen(false);
      
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
      
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task details');
      isUpdatingRef.current = false;
    }
  };

  const closeTaskDetails = () => {
    setIsDetailsOpen(false);
    setIsEditing(false);
    setSelectedTask(null);
  };

  const updateTaskAssignment = async (taskId, assigneeId) => {
    // Check if assignment actually changed
    const currentTask = selectedTask;
    const currentAssignee = currentTask.assigned_to || 'unassigned';
    const newAssignee = assigneeId || 'unassigned';
    
    if (currentAssignee === newAssignee) {
      console.log('Assignment unchanged, skipping update');
      return;
    }
    
    try {
      isUpdatingRef.current = true;
      const token = localStorage.getItem('session_token');
      const response = await axios.patch(`${API}/tasks/${taskId}`, 
        { assigned_to: assigneeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.pending) {
        toast.info('Reassignment submitted for approval');
      } else {
        toast.success('Task reassigned successfully');
      }
      
      await fetchTasks();
      
      // Notify other components
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
      
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign task');
      isUpdatingRef.current = false;
    }
  };

  const updateTask = async () => {
    try {
      isUpdatingRef.current = true;
      const token = localStorage.getItem('session_token');
      const response = await axios.patch(`${API}/tasks/${selectedTask.id}`, 
        { 
          status: editedTask.status
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.pending) {
        toast.info('Status change submitted for approval');
      } else {
        toast.success('Task updated successfully');
      }
      
      setIsEditing(false);
      await fetchTasks();
      setIsDetailsOpen(false);
      
      // Notify other components
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
      
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
      isUpdatingRef.current = false;
    }
  };

  const deleteTask = async (taskId) => {
    setConfirmDelete(null);
    try {
      isUpdatingRef.current = true;
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted successfully');
      await fetchTasks();
      setIsDetailsOpen(false);
      
      // Notify other components
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
      
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    } catch (error) {
      toast.error('Failed to delete task');
      isUpdatingRef.current = false;
    }
  };

  const filterTasks = (taskList) => {
    return taskList.filter(task => {
      const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           task.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesAssignee = filterAssignee === 'all' || 
                             (filterAssignee === 'unassigned' && !task.assigned_to) ||
                             task.assigned_to === filterAssignee;
      return matchesSearch && matchesAssignee;
    });
  };

  const getMemberName = (memberId) => {
    const member = teamMembers.find(m => m.id === memberId);
    return member ? member.name : 'Unassigned';
  };

  const getTaskStats = () => {
    const allTasks = [...tasks.todo, ...tasks['in-progress'], ...tasks.done];
    return {
      total: allTasks.length,
      completed: tasks.done.length,
      inProgress: tasks['in-progress'].length,
      todo: tasks.todo.length
    };
  };

  const stats = getTaskStats();

  if (!selectedTeam) {
    return (
      <div className="page-header">
        <h1 className="page-title">Kanban Board</h1>
        <p className="page-subtitle">Create a team to start managing tasks</p>
      </div>
    );
  }

  return (
    <div className="kanban-view" data-testid="kanban-view">
      <div className="page-header">
        <div>
          <h1 className="page-title">Kanban Board</h1>
          <p className="page-subtitle">Visualize and manage your tasks</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="w-[200px]" data-testid="team-selector">
              <SelectValue placeholder="Select team" />
            </SelectTrigger>
            <SelectContent>
              {teams.map(team => (
                <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) voiceCapture.clear(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-task-button">Add Task</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-task-dialog">
              <DialogHeader>
                <DialogTitle>Create New Task</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Task title"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  data-testid="task-title-input"
                />
                <div className="describe-row compact">
                  <Textarea
                    placeholder="Task description — or tap the mic and describe one or several tasks"
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    rows={3}
                    data-testid="task-description-input"
                  />
                  <VoiceCaptureButton capture={voiceCapture} disabled={!selectedTeam} />
                </div>
                <div className="extras-grid">
                  <label className="draft-field">
                    <span>Column</span>
                    <Select value={newTask.status} onValueChange={(value) => setNewTask({ ...newTask, status: value })}>
                      <SelectTrigger data-testid="task-status-selector"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">To Do</SelectItem>
                        <SelectItem value="in-progress">In Progress</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                  <label className="draft-field">
                    <span>Assignee</span>
                    <Select value={newTask.assigned_to || 'unassigned'} onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value === 'unassigned' ? '' : value })}>
                      <SelectTrigger data-testid="assign-to-selector">
                        <SelectValue placeholder="Assign to (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {teamMembers.map(member => (
                          <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                </div>
                <TaskExtrasEditor value={newTask} onChange={setNewTask} testPrefix="new-task" />
                <Button onClick={createTask} disabled={!newTask.title.trim()} data-testid="create-task-submit">Create Task</Button>
                <TaskDraftList capture={voiceCapture} teams={teams} user={user} compact onCreated={() => fetchTasks()} />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="stats-container">
        <Card className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #1a936f 0%, #0f6b4f 100%)' }}>
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Total Tasks</p>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #26a17b 0%, #1a936f 100%)' }}>
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.inProgress}</h3>
            <p>In Progress</p>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #33b087 0%, #26a17b 100%)' }}>
            <User size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.todo}</h3>
            <p>To Do</p>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #40bf93 0%, #33b087 100%)' }}>
            <Filter size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.completed}</h3>
            <p>Completed</p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={20} />
          <Input
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Members</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {teamMembers.map(member => (
              <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="kanban-columns">
          {['todo', 'in-progress', 'done'].map(columnId => {
            const filteredTasks = filterTasks(tasks[columnId]);
            return (
              <Droppable key={columnId} droppableId={columnId}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`kanban-column ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                    data-testid={`kanban-column-${columnId}`}
                  >
                    <div className="column-header">
                      <h3>{columnId.replace('-', ' ').toUpperCase()}</h3>
                      <Badge variant="secondary" className="task-count">{filteredTasks.length}</Badge>
                    </div>
                    
                    <div className="column-content">
                      {filteredTasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`task-card ${snapshot.isDragging ? 'dragging' : ''}`}
                              data-testid={`task-card-${task.id}`}
                              role="button"
                              aria-label={`Open task: ${task.title}`}
                              onClick={(e) => { if (!e.defaultPrevented) openTaskDetails(task); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.target === e.currentTarget) { e.preventDefault(); openTaskDetails(task); }
                              }}
                            >
                              <div className="task-card-header">
                                <h4>{task.title}</h4>
                                <button 
                                  type="button"
                                  className="task-menu-button"
                                  aria-label="Open task details"
                                  data-testid={`task-menu-${task.id}`}
                                  onClick={(e) => { e.stopPropagation(); openTaskDetails(task); }}
                                >
                                  <MoreVertical size={16} />
                                </button>
                              </div>
                              <p>{task.description}</p>
                              <TaskMeta task={task} />
                              {task.subtasks?.length > 0 && (
                                <div className="task-card-subtasks" data-testid={`task-subtasks-${task.id}`}>
                                  <span className="subtask-bar"><span style={{ width: `${(task.subtasks.filter((s) => s.done).length / task.subtasks.length) * 100}%` }} /></span>
                                  <span>{task.subtasks.filter((s) => s.done).length}/{task.subtasks.length}</span>
                                </div>
                              )}
                              <div className="task-card-footer">
                                {task.assigned_to && (
                                  <div className="task-assignee">
                                    <User size={14} />
                                    <span>{getMemberName(task.assigned_to)}</span>
                                  </div>
                                )}
                                <span className="task-date">
                                  {new Date(task.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>

      {/* Task Details Dialog */}
      {selectedTask && (
        <Dialog open={isDetailsOpen} onOpenChange={(open) => !open && closeTaskDetails()}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="task-details-dialog">
            <DialogHeader>
              <div className="details-head">
                <DialogTitle>Task Details</DialogTitle>
                <ListenButton
                  id={`task-${selectedTask.id}`}
                  label="Read task aloud"
                  text={taskSpeechText(selectedTask, selectedTask.assigned_to ? getMemberName(selectedTask.assigned_to) : null)}
                  testId="read-task-aloud"
                />
              </div>
            </DialogHeader>
            
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="comments">
                  <MessageSquare size={16} className="mr-2" />
                  Comments
                </TabsTrigger>
                <TabsTrigger value="ai">
                  <Sparkles size={16} className="mr-2" />
                  AI Features
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="task-details-content">
                <div className="detail-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4>Title</h4>
                    {!isEditingDetails && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setIsEditingDetails(true)}
                        style={{ padding: '0.25rem' }}
                      >
                        <Edit size={16} />
                      </Button>
                    )}
                  </div>
                  {isEditingDetails ? (
                    <Input
                      value={editedTask.title}
                      onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                      placeholder="Task title"
                    />
                  ) : (
                    <p className="detail-text">{selectedTask.title}</p>
                  )}
                </div>
                <div className="detail-section">
                  <h4>Description</h4>
                  {isEditingDetails ? (
                    <Textarea
                      value={editedTask.description}
                      onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                      placeholder="Task description"
                      rows={4}
                    />
                  ) : (
                    <p className="detail-text">{selectedTask.description}</p>
                  )}
                </div>
                {isEditingDetails ? (
                  <div className="detail-section">
                    <h4>Planning</h4>
                    <TaskExtrasEditor value={editedTask} onChange={setEditedTask} testPrefix="edit-task" />
                  </div>
                ) : (
                  <>
                    <div className="detail-section">
                      <h4>Planning</h4>
                      <TaskMeta task={selectedTask} />
                      {!selectedTask.priority && !selectedTask.due_date && selectedTask.estimated_hours == null && (
                        <p className="detail-text muted">No priority, due date or estimate set.</p>
                      )}
                    </div>
                    <div className="detail-section">
                      <h4>Subtasks</h4>
                      <SubtaskChecklist
                        task={selectedTask}
                        onChanged={(updated) => {
                          setSelectedTask(updated);
                          setTasks((prev) => Object.fromEntries(Object.entries(prev).map(([col, list]) => [col, list.map((t) => (t.id === updated.id ? updated : t))])));
                        }}
                      />
                    </div>
                  </>
                )}
                <div className="detail-section">
                  <h4>Status</h4>
                  {isEditing ? (
                    <Select 
                      value={editedTask.status}
                      onValueChange={(value) => setEditedTask({ ...editedTask, status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">To Do</SelectItem>
                        <SelectItem value="in-progress">In Progress</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge>{selectedTask.status.replace('-', ' ')}</Badge>
                  )}
                </div>
                <div className="detail-section">
                  <h4>Assigned To</h4>
                  <Select 
                    value={selectedTask.assigned_to || 'unassigned'} 
                    onValueChange={(value) => updateTaskAssignment(selectedTask.id, value === 'unassigned' ? null : value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {teamMembers.map(member => (
                        <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="detail-section">
                  <h4>Created</h4>
                  <p className="detail-text">{new Date(selectedTask.created_at).toLocaleString()}</p>
                </div>
                <div className="detail-section">
                  <h4>Last Updated</h4>
                  <p className="detail-text">{new Date(selectedTask.updated_at).toLocaleString()}</p>
                </div>
                <div className="detail-section" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
                  {isEditingDetails ? (
                    <>
                      <Button onClick={updateTaskDetails} style={{ flex: 1 }}>
                        <Save size={16} style={{ marginRight: '0.5rem' }} />
                        Save Details
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setIsEditingDetails(false);
                        setEditedTask({ title: selectedTask.title, description: selectedTask.description, status: selectedTask.status, ...editableExtras(selectedTask) });
                      }} style={{ flex: 1 }}>
                        <X size={16} style={{ marginRight: '0.5rem' }} />
                        Cancel
                      </Button>
                    </>
                  ) : isEditing ? (
                    <>
                      <Button onClick={updateTask} style={{ flex: 1 }} data-testid="save-task-button">
                        <Save size={16} style={{ marginRight: '0.5rem' }} />
                        Save Status
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditing(false)} style={{ flex: 1 }}>
                        <X size={16} style={{ marginRight: '0.5rem' }} />
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        onClick={() => setIsEditing(true)}
                        data-testid="edit-task-button"
                        style={{ flex: 1 }}
                      >
                        <Edit size={16} style={{ marginRight: '0.5rem' }} />
                        Edit Status
                      </Button>
                      {isOwner && (
                        <Button 
                          variant="destructive" 
                          onClick={() => setConfirmDelete(selectedTask.id)}
                          data-testid="delete-task-button"
                          style={{ flex: 1 }}
                        >
                          <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                          Delete
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="comments" className="mt-4">
                {selectedTask && (
                  <CommentThread 
                    taskId={selectedTask.id} 
                    currentUser={user}
                  />
                )}
              </TabsContent>
              
              <TabsContent value="ai" className="mt-4">
                {selectedTask && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* AI Sentiment & Prediction */}
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <AISentimentIndicator taskId={selectedTask.id} />
                    </div>
                    
                    {/* AI Task Prediction */}
                    <AITaskPrediction taskId={selectedTask.id} />
                    
                    {/* AI Comment Summary */}
                    <div>
                      <h4 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Comment Summary</h4>
                      <AICommentSummary taskId={selectedTask.id} />
                    </div>
                    
                    {/* AI Status Update Generator */}
                    <div>
                      <h4 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Generate Status Update</h4>
                      <AIStatusUpdate taskId={selectedTask.id} />
                    </div>
                    
                    {/* AI Task Breakdown */}
                    <div>
                      <h4 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Task Breakdown</h4>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowAIBreakdown(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      >
                        <Sparkles size={16} />
                        Break Down Task
                      </Button>
                    </div>
                    
                    {/* AI Assignee Suggestion */}
                    <div>
                      <h4 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Smart Assignment</h4>
                      <AISuggestAssignee 
                        taskId={selectedTask.id} 
                        onSelect={async (userId) => {
                          await updateTaskAssignment(selectedTask.id, userId);
                          toast.success('Task assigned based on AI suggestion');
                        }}
                      />
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
            
            {/* AI Breakdown Dialog */}
            {showAIBreakdown && selectedTask && (
              <AITaskBreakdown 
                taskId={selectedTask.id} 
                onClose={() => setShowAIBreakdown(false)} 
              />
            )}
            <ConfirmDialog
              open={Boolean(confirmDelete)}
              onOpenChange={(open) => !open && setConfirmDelete(null)}
              title="Delete this task?"
              description="The task and its comments will be removed for everyone on the team. This cannot be undone."
              onConfirm={() => deleteTask(confirmDelete)}
              testId="delete-task-confirm"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
